import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Zap, X, AlertTriangle } from 'lucide-react';

// Public checkout result page — does NOT use the AuthContext or send cookies.
// Reached at /checkout/success and /checkout/cancel, ALWAYS on the app's own
// domain (never back on the /buy/{slug} page or a staff-shared-link origin),
// so both the public buy page and internally-generated Stripe links land here.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public`,
  headers: { 'Content-Type': 'application/json' },
});

const fmt = (n, ccy = 'usd') => `${(ccy || 'usd').toUpperCase()} ${Number(n).toFixed(2)}`;

const CheckoutResult = ({ mode }) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [order, setOrder] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [productSlug, setProductSlug] = useState('');

  useEffect(() => {
    const url = new URL(window.location.href);
    if (mode === 'cancel') {
      setProductSlug(url.searchParams.get('slug') || '');
      setState('ready');
      return;
    }
    const sid = url.searchParams.get('session_id');
    if (!sid) { setState('error'); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await publicApi.get(`/commerce/checkout/status/${sid}`);
        if (cancelled) return;
        if (data.status === 'paid') {
          setOrder(data);
          try {
            const { data: ups } = await publicApi.get(`/commerce/upsell/${data.id}`);
            if (ups.available) setUpsell(ups);
          } catch { /* upsell lookup is best-effort */ }
          setState('ready');
          return;
        }
        if (data.status === 'expired' || attempts >= 12) { setState('error'); return; }
      } catch {
        if (attempts >= 12) { setState('error'); return; }
      }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [mode]);

  if (state === 'loading') {
    return (
      <div className="fp-shell">
        <div className="fp-card" data-testid="checkout-result-loading">
          <Loader2 className="spin" size={20} /> Confirming your payment…
        </div>
      </div>
    );
  }

  if (mode === 'cancel') {
    return (
      <div className="fp-shell">
        <div className="fp-card" data-testid="checkout-cancelled">
          <div className="ic" style={{ color: '#ec4646' }}><XCircle size={44} /></div>
          <div className="fp-title" style={{ marginBottom: 6 }}>Checkout cancelled</div>
          <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>
            No charge was made. You can pick up right where you left off whenever you're ready.
          </div>
          {productSlug && (
            <a
              href={`/buy/${productSlug}`}
              className="fp-submit"
              style={{ display: 'inline-block', marginTop: 16, textDecoration: 'none', textAlign: 'center' }}
              data-testid="checkout-cancel-retry"
            >
              Try again
            </a>
          )}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="fp-shell">
        <div className="fp-card" data-testid="checkout-result-error">
          <div className="ic" style={{ color: '#e8a33d' }}><AlertTriangle size={44} /></div>
          <div className="fp-title" style={{ marginBottom: 6 }}>We couldn't confirm this payment</div>
          <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>
            If you were charged, don't worry — reach out to us and we'll sort it out. If you weren't,
            no payment was taken.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-shell">
      <div className="fp-card fp-success" data-testid="checkout-success">
        <div className="ic"><CheckCircle2 size={44} /></div>
        <div className="fp-title" style={{ marginBottom: 6 }}>Payment received</div>
        <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>
          Thanks for purchasing <b>{order.product_name}</b>. A confirmation has been sent to your email.
        </div>
        {order.receipt_url && (
          <a href={order.receipt_url} target="_blank" rel="noreferrer" className="btn-link" style={{ display: 'inline-block', marginTop: 8 }}>
            View receipt →
          </a>
        )}
      </div>
      {upsell && <UpsellCard orderId={order.id} upsell={upsell} />}
    </div>
  );
};

const UpsellCard = ({ orderId, upsell }) => {
  const [state, setState] = useState('offer'); // offer | charging | done | declined | error
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const accept = async () => {
    setState('charging'); setErr('');
    try {
      const { data } = await publicApi.post('/commerce/upsell/charge', { order_id: orderId, accept: true });
      setResult(data); setState('done');
    } catch (e) { setErr(e?.response?.data?.detail || 'Charge failed'); setState('error'); }
  };
  const decline = () => setState('declined');

  if (state === 'declined') return null;
  if (state === 'done') {
    return (
      <div className="fp-card" style={{ marginTop: 16 }} data-testid="upsell-done">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64cb13' }}><CheckCircle2 size={18} /><b>{result.product_name} added!</b></div>
        <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 4 }}>{fmt(result.amount, result.currency)} charged to your card on file.</div>
        {result.receipt_url && <a href={result.receipt_url} target="_blank" rel="noreferrer" className="btn-link">View receipt →</a>}
      </div>
    );
  }

  return (
    <div className="fp-card" style={{ marginTop: 16, border: '2px solid #ff9200', boxShadow: '0 8px 20px -8px rgba(255,146,0,0.3)' }} data-testid="upsell-offer">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 10, background: '#ff9200', color: '#1a1208', fontSize: 10, fontWeight: 700, marginBottom: 10 }}>
        <Zap size={10} /> LIMITED-TIME OFFER · ONE CLICK
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Add {upsell.product.name}</div>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{upsell.product.description}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 10 }}>
        <span style={{ fontSize: 24, fontWeight: 700 }}>{fmt(upsell.discounted_price, upsell.product.currency)}</span>
        {upsell.discount_pct > 0 && <span style={{ fontSize: 12, color: 'var(--text-faded)', textDecoration: 'line-through' }}>{fmt(upsell.original_price, upsell.product.currency)}</span>}
        {upsell.discount_pct > 0 && <span className="tag-pill ok">{upsell.discount_pct}% off</span>}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-faded)', marginTop: 4 }}>One click. Uses your card on file. No re-entering details.</div>
      {err && <div className="f-error">{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
        <button className="fp-submit" onClick={accept} disabled={state === 'charging'} data-testid="accept-upsell" style={{ flex: 2 }}>
          {state === 'charging' ? 'Charging…' : `Yes, add it for ${fmt(upsell.discounted_price, upsell.product.currency)}`}
        </button>
        <button className="btn-secondary" onClick={decline} type="button" data-testid="decline-upsell" style={{ flex: 1 }}><X size={12} style={{ marginRight: 4 }} />No thanks</button>
      </div>
    </div>
  );
};

export default CheckoutResult;
